import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Typography,
  Avatar,
  Grid,
  Modal,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TabLevelData } from "./TabLevelData";
import VisibilityIcon from "@mui/icons-material/Visibility";

export const FarmerLevelTab = ({
  formType,
  documentId,
  stateValue,
  districtValue,
  blockValue,
}) => {
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loadingTabs, setLoadingTabs] = useState(true);
  const [activeApiUrl, setActiveApiUrl] = useState(null);
  const [cropLevelData, setCropLevelData] = useState(null);
  const [loadingCropData, setLoadingCropData] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [cropDetail, setCropDetail] = useState(null);

  const fetchCropLevelData = async (farmerId) => {
    setLoadingCropData(true);
    try {
      const res = await axios.get(
        `https://agrani-cms.leadsconnect.in/api/farmer-level-crops?fields[2]=cropping_seasons_for_this_commodity&fields[3]=crop_group&fields[4]=crop_name&fields[5]=farmer_id&filters[farmer_id]=${farmerId}&pagination[page]=1&pagination[pageSize]=1000`
      );
      setCropLevelData(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching Crop Level data:", err);
      setCropLevelData([]);
    } finally {
      setLoadingCropData(false);
    }
  };

  const fetchCropDetail = async (farmerId, cropId) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(
        `https://agrani-cms.leadsconnect.in/api/farmer-level-crops?filters[farmer_id]=${farmerId}`
      );
      const allData = res.data?.data || [];
      const matched = allData.find((item) => item.id === cropId);
      setCropDetail(matched || null);
    } catch (err) {
      console.error("Error fetching crop detail:", err);
      setCropDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    const fetchTabs = async () => {
      try {
        const res = await axios.get(
          "https://agrani-cms.leadsconnect.in/api/valuechain-survey-forms?sort=label"
        );
        const data = res.data?.data || [];
        const filtered = data.filter((item) => item.form_type === formType);
        const uniqueTabs = Array.from(
          new Map(filtered.map((item) => [item.label.trim(), item])).values()
        );

        setTabs(uniqueTabs);

        if (uniqueTabs.length > 0) {
          const firstTab = uniqueTabs[0];
          if (firstTab.label === "Crop Level") {
            fetchCropLevelData(documentId);
          } else {
            setActiveApiUrl(firstTab.get_api_url);
          }
        }
      } catch (error) {
        console.error("Error fetching tabs:", error);
      } finally {
        setLoadingTabs(false);
      }
    };

    fetchTabs();
  }, [formType, documentId]);

  const handleChange = async (event, newValue) => {
    setSelectedTab(newValue);
    const tab = tabs[newValue];

    if (tab?.label === "Crop Level") {
      fetchCropLevelData(documentId);
    } else {
      setActiveApiUrl(tab?.get_api_url || null);
      setCropLevelData(null);
    }
  };

  const handleOpenModal = (crop) => {
    setSelectedCrop(crop);
    setOpenModal(true);
    fetchCropDetail(documentId, crop.id);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCropDetail(null);
  };

  if (loadingTabs) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tabs.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <Typography variant="h6" color="text.secondary">
          🚫 No Data Available for <strong>{formType}</strong>
        </Typography>
      </Box>
    );
  }

  console.log(documentId, "iddd");
  const formatKey = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <Box>
      <Tabs
        value={selectedTab}
        onChange={handleChange}
        variant="scrollable" // ✅ makes it scrollable
        scrollButtons="auto" // ✅ shows arrows when needed
        textColor="primary"
        indicatorColor="primary"
        sx={{ borderBottom: "1px solid #ddd" }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  src={tab.icon_url}
                  alt={tab.label}
                  sx={{ width: 28, height: 28 }}
                />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {tab.label}
                </Typography>
              </Box>
            }
          />
        ))}
      </Tabs>

      <Box
        sx={{
          p: { xs: 1, sm: 1, lg: 3 },
        }}
      >
        {tabs[selectedTab]?.label === "Crop Level" ? (
          loadingCropData ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : cropLevelData?.length > 0 ? (
            <Grid container spacing={2} className="mt-1">
              {cropLevelData.map((item) => (
                <Grid item xs={12} md={4} key={item.id} className="">
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid #ddd",
                      borderRadius: "10px",
                      height: "100%",
                      cursor: "pointer",
                      position: "relative",
                      "&:hover": { boxShadow: 2 },
                    }}
                    onClick={() => handleOpenModal(item)}
                  >
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "orange",
                      }}
                      size="small"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>

                    <Typography variant="body1" gutterBottom>
                      🌱 <strong>Crop Name:</strong> {item?.crop_name}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      🌾 <strong>Crop Group:</strong> {item?.crop_group}
                    </Typography>
                    {/* <Typography variant="body2" gutterBottom>
                      📏 <strong>Landholding:</strong>{" "}
                      {item?.total_landholding_in_hectares} hectares
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      🕒 <strong>Years in Farming:</strong>{" "}
                      {item?.years_involved_in_farming}
                    </Typography> */}
                    <Typography variant="body2">
                      🌦 <strong>Seasons:</strong>{" "}
                      {item?.cropping_seasons_for_this_commodity?.[0]}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography
              color="text.secondary"
              className="d-flex justify-content-center align-items-center p-5"
            >
              🚫 No Crop Data Available
            </Typography>
          )
        ) : activeApiUrl ? (
          <TabLevelData farmerId={documentId} apiUrl={activeApiUrl} />
        ) : (
          <Typography
            color="text.secondary"
            className="d-flex justify-content-center align-items-center p-5"
          >
            No data available
          </Typography>
        )}
      </Box>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: 0,
            top: 20,
            position: "absolute",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle>
          <h4>🌱 Crop Details</h4>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : cropDetail ? (
            <div className="row g-3">
              {Object.entries(cropDetail).map(([key, value]) => {
                if (
                  key === "id" ||
                  key === "documentId" ||
                  key === "createdAt" ||
                  key === "updatedAt" ||
                  key === "publishedAt" ||
                  key === "farmer_id"
                )
                  return null;

                // if (
                //   value === "" ||
                //   value === null ||
                //   (Array.isArray(value) && value.length === 0)
                // ) {
                //   return null;
                // }

                let displayValue = "";
                if (Array.isArray(value)) {
                  displayValue = value.join(", ");
                } else if (typeof value === "object" && value !== null) {
                  displayValue = JSON.stringify(value);
                } else {
                  displayValue = value?.toString() || "-";
                }

                return (
                  <div className="col-md-6 col-lg-4" key={key}>
                    <div className="border rounded-3 shadow-sm p-3 bg-white">
                      <div className="d-flex align-items-start ">
                        <i className="mdi mdi-chevron-double-right text-success me-2 fs-5"></i>
                        <span className="fw-semibold text-dark mt-1">
                          {formatKey(key)}
                        </span>
                      </div>

                      <div className="d-flex align-items-center">
                        <i className="mdi mdi-chevron-right me-2 fs-5"></i>
                        <span className="text-gray">{displayValue}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <Typography>🚫 No details found</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
